import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import { motion } from 'framer-motion';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCookie } from 'react-use';

import sendGravityFormData from 'utils/send-gravity-form-data';
import sendGtagEvent from 'utils/send-gtag-event';

import Modal from 'components/shared/modal';
import Heading from 'components/shared/heading';
import Input from 'components/shared/input';
import Button from 'components/shared/button';

import { MODAL_DOWNLOAD_FORM_ID } from 'constants';
import CheckIcon from './images/check.inline.svg';
import Illustration from './images/illustration.inline.svg';

import styles from './modal-download-form.module.scss';

const cx = classNames.bind(styles);

const validationSchema = yup.object().shape({
  email: yup.string().trim().email('Must be a valid email').required('Email is a required field'),
});

const KEY_LOCAL_STORAGE = 'modal-download-form';

const DELAY_OPEN_MODAL = 10000; // milliseconds
// It is used for proper loading animation because most of the time we get response from the server almost immediately
const APPEAR_AND_EXIT_ANIMATION_DURATION = 0.5; // seconds

const ModalDownloadForm = (props) => {
  const {
    id,
    postId,
    fileUrl,
    title,
    description,
    inputPlaceholder,
    buttonText,
    successMessage: { title: messageTitle, description: messageDescription },
  } = props;

  const [isOpen, setIsOpen] = useState(false);

  const { register, handleSubmit, errors } = useForm({
    resolver: yupResolver(validationSchema),
  });

  const [isLoading, setIsLoading] = useState(false);
  const [serverResponse, setServerResponse] = useState(null);
  // HubSpot cookie tracking
  // read more: https://legacydocs.hubspot.com/docs/methods/forms/submit_form
  const [hubspotutk] = useCookie('hubspotutk');

  const getLocalStorage = () => JSON.parse(localStorage.getItem(KEY_LOCAL_STORAGE));
  const setLocalStorage = (id) => {
    const identifiers = getLocalStorage() || { [id]: true };

    if (!(id in identifiers)) {
      identifiers[id] = true;
    }

    localStorage.setItem(KEY_LOCAL_STORAGE, JSON.stringify(identifiers));
  };

  const handleCloseModal = () => {
    setIsOpen(false);
    setLocalStorage(id);
    sendGtagEvent('modal-download-closed');
  };

  const handleOpenModal = () => {
    setTimeout(() => {
      setIsOpen(true);
    }, DELAY_OPEN_MODAL);
  };

  const onSubmit = async (values) => {
    setIsLoading(true);
    const gclid = localStorage.getItem('gclid')
      ? JSON.parse(localStorage.getItem('gclid')).value
      : '';
    const slcode = localStorage.getItem('slcode')
      ? JSON.parse(localStorage.getItem('slcode')).value
      : '';

    try {
      await sendGravityFormData(MODAL_DOWNLOAD_FORM_ID, {
        input_1: values.email,
        input_3: window.location.pathname,
        input_5: postId,
        input_6: fileUrl,
        input_7: gclid,
        input_8: slcode,
        input_9: hubspotutk,
      });

      setIsLoading(false);
      setServerResponse('success');
      setLocalStorage(id);
      localStorage.removeItem('gclid');
      localStorage.removeItem('slcode');
    } catch {
      setIsLoading(false);
      setServerResponse('error');
    }
  };

  useEffect(() => {
    if (!getLocalStorage() || !getLocalStorage()[id]) {
      handleOpenModal();
    }
  }, []);

  return (
    <>
      <Modal
        isOpen={isOpen}
        onCloseButtonClick={handleCloseModal}
        onRequestClose={handleCloseModal}
      >
        <div className={cx('wrapper')}>
          <motion.div
            className={cx('inner')}
            animate={
              serverResponse === 'success' && {
                opacity: 0,
                height: 0,
                visibility: 'hidden',
                transition: { duration: APPEAR_AND_EXIT_ANIMATION_DURATION },
              }
            }
          >
            <Illustration className={cx('illustration')} />
            <Heading className={cx('title')} size="lg">
              {title}
            </Heading>
            <p className={cx('description')}>{description}</p>
            <form className={cx('form')} onSubmit={handleSubmit(onSubmit)} noValidate>
              <Input
                className={cx('input')}
                name="email"
                type="email"
                placeholder={inputPlaceholder}
                autoComplete="email"
                error={errors?.email?.message}
                withBorder
                requiredStyle
                ref={register}
              />
              <Button
                className={cx('button')}
                type="submit"
                theme="accent-primary"
                loading={isLoading}
              >
                {buttonText}
              </Button>
            </form>
          </motion.div>

          {serverResponse === 'success' && (
            <motion.div
              className={cx('message')}
              initial={{ opacity: 0 }}
              animate={{
                opacity: 1,
                transition: { delay: APPEAR_AND_EXIT_ANIMATION_DURATION },
              }}
              aria-hidden
            >
              <CheckIcon className={cx('message__icon')} />
              <Heading className={cx('message__title')} tag="p" size="lg">
                {messageTitle}
              </Heading>
              <p className={cx('message__description')}>{messageDescription}</p>
            </motion.div>
          )}
        </div>
      </Modal>
    </>
  );
};

ModalDownloadForm.defaultProps = {
  inputPlaceholder: 'Email...',
  buttonText: 'Download',
  successMessage: {
    title: 'Success!',
    description: 'We will be in touch with you shortly.',
  },
};

ModalDownloadForm.propTypes = {
  id: PropTypes.string.isRequired,
  postId: PropTypes.number.isRequired,
  fileUrl: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  inputPlaceholder: PropTypes.string,
  buttonText: PropTypes.string,
  successMessage: PropTypes.objectOf(PropTypes.any),
};

export default ModalDownloadForm;
